/**
 * @file Dashboard page.
 * @description Shows KPI cards, charts, recent uploads, and quick navigation actions for lead operations.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users, Upload, Copy, Target,
  TrendingUp, Activity, Download,
} from 'lucide-react';
import DashboardCard from '../components/DashboardCard';
import { PageHeader, Panel } from '../components/ui';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend,
  ArcElement, BarElement, Filler,
} from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { DashboardService } from '../services/dashboardService';
import { DashboardData, UploadHistory } from '../types';

ChartJS.register(
  CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend,
  ArcElement, BarElement, Filler,
);
const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [recentUploads, setRecentUploads] = useState<UploadHistory[]>([]);
  // Pagination state for Recent Uploads
  const [uploadsPage, setUploadsPage] = useState(1);
  const uploadsPerPage = 5;
  const totalUploads = recentUploads.length;
  const totalPages = Math.max(1, Math.ceil(totalUploads / uploadsPerPage));

  const paginatedUploads = useMemo(() => {
    return recentUploads.slice(
      (uploadsPage - 1) * uploadsPerPage,
      uploadsPage * uploadsPerPage,
    );
  }, [recentUploads, uploadsPage]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch both dashboard data and history in parallel
      const { dashboard, history } = await DashboardService.getDashboardDataWithHistory();
      setDashboardData(dashboard);
      setRecentUploads(history.uploadHistory || []);
      setUploadsPage(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);



  // --- Chart Data ---
  const leadTrendData = useMemo(() => ({
    labels: dashboardData?.leadTrend.map(d =>
      new Date(`${d.month}-02`).toLocaleString('default', { month: 'short' }),
    ) || [],
    datasets: [
      {
        label: 'Leads Uploaded',
        data: dashboardData?.leadTrend.map(d => Number.parseInt(d.lead_count, 10)) || [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  }), [dashboardData]);

  const leadQualityData = useMemo(() => ({
    labels: dashboardData?.leadSources.map(s => s.source) || [],
    datasets: [
      {
        data: dashboardData?.leadSources.map(s => Number.parseInt(s.lead_count, 10)) || [],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(168, 85, 247, 0.8)',
        ],
        borderWidth: 0,
      },
    ],
  }), [dashboardData]);

  const campaignPerformanceData = useMemo(() => ({
    labels: dashboardData?.campaignPerformance.map(c => c.name) || [],
    datasets: [
      {
        label: 'Leads',
        data: dashboardData?.campaignPerformance.map(c => Number.parseInt(c.lead_count, 10)) || [],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
      },
    ],
  }), [dashboardData]);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' as const },
    },
    scales: { y: { beginAtZero: true } },
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Page Header Skeleton */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-purple-500 mr-4 animate-pulse">
                <div className="h-8 w-8"></div>
              </div>
              <div>
                <div className="h-8 bg-purple-400 rounded w-48 mb-2 animate-pulse"></div>
                <div className="h-4 bg-purple-400 rounded w-64 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-gray-100 animate-pulse">
                  <div className="h-6 w-6"></div>
                </div>
                <div className="ml-4">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="h-6 bg-gray-200 rounded w-32 mb-4 animate-pulse"></div>
            <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="h-6 bg-gray-200 rounded w-32 mb-4 animate-pulse"></div>
            <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Loading Spinner */}
        <div className="flex justify-center items-center py-8">
          <div className="flex items-center space-x-2 text-gray-600">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
            <span className="text-lg font-medium">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-purple-500 mr-4">
                <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
                <p className="text-purple-100">Overview of your lead management system</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Card */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Dashboard</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchData}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle="Welcome to JIGGLE Lead Management System"
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <DashboardCard
          title="Total Leads"
          value={parseInt(dashboardData?.kpis.totalLeads || '0').toLocaleString()}
          icon={Users}
          gradient="from-blue-500 to-blue-600"
        />
        <DashboardCard
          title="Total Records"
          value={parseInt(dashboardData?.kpis.totalRecords || '0').toLocaleString()}
          icon={Upload}
          gradient="from-indigo-500 to-indigo-600"
        />
        <DashboardCard
          title="Duplicates"
          value={parseInt(dashboardData?.kpis.totalDuplicates || '0').toLocaleString()}
          icon={Copy}
          change="Calculated on upload"
          changeType="neutral"
          gradient="from-accent-500 to-accent-600"
        />
        <DashboardCard
          title="Total Advertisers"
          value={parseInt(dashboardData?.kpis.totalAdvertisers || '0').toLocaleString()}
          icon={Target}
          gradient="from-secondary-500 to-secondary-600"
        />
        <DashboardCard
          title="Active Campaigns"
          value={parseInt(dashboardData?.kpis.activeCampaigns || '0').toLocaleString()}
          icon={Activity}
          gradient="from-green-500 to-green-600"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Panel padded>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Upload Trend (Last 6 Months)</h3>
          <Line data={leadTrendData} options={chartOptions} />
        </Panel>
        <Panel padded>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Leads by Source</h3>
          <div className="w-64 mx-auto">
            <Doughnut
              data={leadQualityData}
              options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }}
            />
          </div>
        </Panel>

        {/* Campaign Performance Chart */}
        <Panel padded>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 5 Campaign Performance</h3>
          <Bar data={campaignPerformanceData} options={chartOptions} />
        </Panel>
      </div>



      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Uploads */}
        <Panel padded>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Uploads</h3>
            <Upload size={20} className="text-gray-400" />
          </div>
          <div className="space-y-4">
            {totalUploads > 0 ? (
              paginatedUploads.map((upload) => (
                <div key={upload.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{upload.filename}</p>
                    <p className="text-sm text-gray-600">
                      {upload.campaign_name} ({upload.advertiser_name})
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(upload.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-600">{upload.valid_records} valid</p>
                    <p className="text-sm text-red-600">{upload.duplicate_records} duplicates</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No recent uploads found.</p>
            )}
          </div>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-4 space-x-2">
              <button
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                onClick={() => setUploadsPage((p) => Math.max(1, p - 1))}
                disabled={uploadsPage === 1}
              >
                Prev
              </button>
              <span className="text-sm text-gray-700">
                Page {uploadsPage} of {totalPages}
              </span>
              <button
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50"
                onClick={() => setUploadsPage((p) => Math.min(totalPages, p + 1))}
                disabled={uploadsPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </Panel>

        {/* Quick Actions */}
        <Panel padded>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            <TrendingUp size={20} className="text-gray-400" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">

            <button
              onClick={() => navigate('/leads/upload')}
              className="group w-full min-h-[150px] flex flex-col items-center justify-center text-center p-4 sm:p-5 bg-gradient-to-br from-primary-50 to-secondary-50 hover:from-primary-100 hover:to-secondary-100 rounded-2xl border border-primary-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary-100 group-hover:bg-primary-200 transition-colors duration-300">
                <Upload size={22} className="text-primary-600 sm:w-6 sm:h-6" />
              </div>

              <span className="mt-3 text-sm sm:text-base font-semibold text-gray-800 tracking-wide">
                Upload New Leads
              </span>
            </button>

            <button
              onClick={() => navigate('/leads/export')}
              className="group w-full min-h-[150px] flex flex-col items-center justify-center text-center p-4 sm:p-5 bg-gradient-to-br from-accent-50 to-red-50 hover:from-accent-100 hover:to-red-100 rounded-2xl border border-accent-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-accent-100 group-hover:bg-accent-200 transition-colors duration-300">
                <Download size={22} className="text-accent-600 sm:w-6 sm:h-6" />
              </div>

              <span className="mt-3 text-sm sm:text-base font-semibold text-gray-800 tracking-wide">
                Export Leads
              </span>
            </button>

            <button
              onClick={() => navigate('/campaigns')}
              className="group w-full min-h-[150px] flex flex-col items-center justify-center text-center p-4 sm:p-5 bg-gradient-to-br from-secondary-50 to-green-50 hover:from-secondary-100 hover:to-green-100 rounded-2xl border border-secondary-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 sm:col-span-2 xl:col-span-1"
            >
              <div className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-secondary-100 group-hover:bg-secondary-200 transition-colors duration-300">
                <Target size={22} className="text-secondary-600 sm:w-6 sm:h-6" />
              </div>

              <span className="mt-3 text-sm sm:text-base font-semibold text-gray-800 tracking-wide">
                Create Campaign
              </span>
            </button>

          </div>
        </Panel>
      </div>
    </div>
  );
};

export default Dashboard;
